import React, { useState, useEffect } from 'react';
import { MdClose, MdSwapHoriz, MdLocationOn, MdGroup, MdPerson, MdBusiness } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';

const ReassignTraderModal = ({ isOpen, onClose, trader, onSuccess }) => {
  const [zones, setZones] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);



  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  useEffect(() => {
    if (isOpen && trader) {
      // Don't pre-populate zone and group - force user to select
      setSelectedZoneId('');
      setSelectedGroupId('');
      setGroups([]); // Clear any existing groups
      setShowConfirmation(false); // Reset confirmation state
      fetchDropdownData();
    }
  }, [isOpen, trader]);

  useEffect(() => {
    if (selectedZoneId) {
      // Clear current group selection when zone changes
      setSelectedGroupId('');
      fetchGroupsForZone(selectedZoneId);
    } else {
      setGroups([]);
      setSelectedGroupId('');
    }
  }, [selectedZoneId]);

  const fetchDropdownData = async () => {
    setDataLoading(true);
    try {
      const userMmdaId = userData.mmda_id || '';

      // Fetch zones
      const zonesRes = await axios.get(`${API_BASE_URL}/zones?userRole=MMDA&mmda_id=${trader.mmda_id}`, API_CONFIG);
      setZones(zonesRes.data);

      // Don't automatically fetch groups - wait for user to select a zone
      // Groups will be loaded only when user changes the zone selection
    } catch (error) {

      setShowPopup({ message: 'Failed to load zones and groups', type: 'error' });
    } finally {
      setDataLoading(false);
    }
  };

  const fetchGroupsForZone = async (zoneId) => {
    setGroupsLoading(true);
    try {
      const userMmdaId = userData.mmda_id || '';
      const url = `${API_BASE_URL}/zonal-groups?userRole=MMDA&mmda_id=${userMmdaId}&zone_id=${zoneId}`;

      const groupsRes = await axios.get(url, API_CONFIG);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
      setShowPopup({ message: 'Failed to load groups for selected zone', type: 'error' });
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedZoneId) {
      setShowPopup({ message: 'Please select a zone', type: 'error' });
      return;
    }

    // Show confirmation dialog first
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    // Proceed with reassignment
    setLoading(true);
    try {
      const updateData = {
        zone_id: parseInt(selectedZoneId),
        group_id: selectedGroupId ? parseInt(selectedGroupId) : null
      };

      console.log('Reassigning trader:', trader.trader_id, 'with data:', updateData);
      const response = await axios.put(`${API_BASE_URL}/traders/${trader.trader_id}`, updateData, API_CONFIG);
      console.log('Reassignment response:', response.data);

      setShowPopup({ message: 'Trader reassigned successfully!', type: 'success' });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error reassigning trader:', error);
      setShowPopup({ message: 'Failed to reassign trader', type: 'error' });
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  if (!isOpen || !trader) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}
    onClick={onClose}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 32,
        width: '90%',
        maxWidth: 600,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
      }}
      onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '2px solid #f0f0f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdSwapHoriz size={28} style={{ color: '#ff9800' }} />
            <div>
              <h2 style={{ margin: 0, color: '#ff9800', fontSize: 22, fontWeight: 'bold' }}>
                Reassign Trader
              </h2>
              <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
                Move trader to a different zone or group
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4,
            }}
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {/* Trader Information */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: 20,
          borderRadius: 8,
          marginBottom: 24,
          border: '1px solid #e9ecef',
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MdBusiness size={20} style={{ color: '#ff9800' }} />
            Trader Details
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Business Name</label>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#333', fontWeight: 'bold' }}>
                {trader.business_name}
              </p>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Owner Name</label>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#333' }}>
                {trader.owner_name}
              </p>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Current Zone</label>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#666' }}>
                {trader.zone_name || 'Not assigned'}
              </p>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Current Group</label>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#666' }}>
                {trader.group_name || 'Not assigned'}
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div style={{
            padding: 20,
            backgroundColor: '#fff3e0',
            borderRadius: 8,
            border: '2px solid #ff9800',
            marginBottom: 20,
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#f57c00', fontSize: 16 }}>
              Confirm Reassignment
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#333' }}>
              Do you want to reassign <strong>{trader?.business_name}</strong> to the selected zone and group?
            </p>
            <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#666' }}>
              This action will move the trader to a new zone/group assignment and update their records.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #6c757d',
                  borderRadius: 4,
                  backgroundColor: 'white',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                No, Cancel
              </button>
              <button
                type="button"
                onClick={handleReassign}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 4,
                  backgroundColor: loading ? '#ccc' : '#ff9800',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                {loading ? 'Reassigning...' : 'Yes, Reassign'}
              </button>
            </div>
          </div>
        )}

        {/* Reassignment Form */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 16 }}>New Assignment</h3>
          
          {dataLoading ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>
              Loading zones and groups...
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 20 }}>
              {/* Zone Selection */}
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: 14
                }}>
                  <MdLocationOn size={16} style={{ color: '#ff9800' }} />
                  Select New Zone *
                </label>
                <select
                  value={selectedZoneId}
                  onChange={(e) => setSelectedZoneId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff9800'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                >
                  <option value="">Select a zone...</option>
                  {zones.map(zone => (
                    <option key={zone.zone_id} value={zone.zone_id}>
                      {zone.zone_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Group Selection */}
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: 14
                }}>
                  <MdGroup size={16} style={{ color: '#ff9800' }} />
                  Select New Group (Optional)
                </label>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={!selectedZoneId || groupsLoading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    backgroundColor: (!selectedZoneId || groupsLoading) ? '#f5f5f5' : 'white',
                    cursor: (!selectedZoneId || groupsLoading) ? 'not-allowed' : 'pointer',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff9800'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                >
                  <option value="">
                    {groupsLoading ? 'Loading groups...' : selectedZoneId ? 'Select a group (optional)' : 'Please select a zone first'}
                  </option>
                  {!groupsLoading && groups.map(group => (
                    <option key={group.group_id} value={group.group_id}>
                      {group.group_name}
                    </option>
                  ))}
                </select>
                {selectedZoneId && !groupsLoading && groups.length === 0 && (
                  <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                    No groups available for this zone
                  </p>
                )}
                {!selectedZoneId && !groupsLoading && (
                  <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                    Please select a zone first to see available groups
                  </p>
                )}
                {groupsLoading && (
                  <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#ff9800', fontStyle: 'italic' }}>
                    Loading groups for selected zone...
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              border: '1px solid #6c757d',
              borderRadius: 6,
              backgroundColor: 'white',
              color: '#6c757d',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 'bold',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleReassign}
            disabled={loading || !selectedZoneId || dataLoading}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: 6,
              backgroundColor: (loading || !selectedZoneId || dataLoading) ? '#ccc' : '#ff9800',
              color: 'white',
              cursor: (loading || !selectedZoneId || dataLoading) ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <MdSwapHoriz size={16} />
            {loading ? 'Reassigning...' : showConfirmation ? 'Confirm Reassignment' : 'Reassign Trader'}
          </button>
        </div>

        {/* Popup Notification */}
        {showPopup && (
          <div style={{
            position: 'fixed',
            top: 20,
            right: 20,
            backgroundColor: showPopup.type === 'success' ? '#d4edda' : '#f8d7da',
            color: showPopup.type === 'success' ? '#155724' : '#721c24',
            padding: 12,
            borderRadius: 4,
            border: `1px solid ${showPopup.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            zIndex: 1001,
            maxWidth: 300,
          }}>
            {showPopup.message}
            <button
              onClick={() => setShowPopup(null)}
              style={{
                marginLeft: 10,
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 'bold',
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReassignTraderModal;
